<script setup lang="ts">
import type { AppMedia } from '~/types/product.model'

defineProps<{
  media: AppMedia[]
}>()
// TODO: check these sizes after gallery-viewer (SC-62443)
const imageWidth = 300
const imageHeight = 168.75

const imageWidthPx = `${imageWidth}px`
const imageHeightPx = `${imageHeight}px`

const imageViewerIndex = ref<number | null>(null)
</script>

<template>
  <section class="app-details-images">
    <h2 class="sr-only">
      Image Gallery
    </h2>
    <div class="app-details-images__image-container">
      <Image
        v-for="(image, i) in media"
        :key="image.id"
        class="app-details-images__image"
        :src="image.url"
        :alt="image.alt"
        :width="imageWidth"
        :height="imageHeight"
        draggable="false"
        preset="detail-page-image"
        @click="imageViewerIndex = i"
      />
      <div class="app-details-images__spacer" />

      <ClientOnly>
        <Teleport to="body">
          <LazyImageViewer
            :media="media"
            :index="imageViewerIndex"
            @close="imageViewerIndex = null"
          />
        </Teleport>
      </ClientOnly>
    </div>
  </section>
</template>

<style lang="scss" scoped>
$image-shadow-width: 16px;

.app-details-images {
  position: relative;
  min-width: calc(100% + $image-shadow-width);

  // we do not want the shadow to be cut on the left side
  transform: translate3d(-$image-shadow-width, 0, 0);

  @include max-width('lg') {
    min-width: 100vw
  }
}

.app-details-images__image-container {
  display: flex;
  justify-content: flex-start;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @include max-width('lg') {
    width: 100vw;
  }
}

.app-details-images__image {
  cursor: pointer;
  object-fit: contain;
  border-radius: var(--app-border-radius-small);
  box-shadow: 0 3px $image-shadow-width 0 var(--app-panel-shadow-color);
  transition: box-shadow 0.25s ease-in-out;
  scroll-snap-align: center;
  scroll-snap-stop: normal;
  margin: 30px $image-shadow-width;
  /* stylelint-disable-next-line value-keyword-case */
  width: v-bind(imageWidthPx);
  /* stylelint-disable-next-line value-keyword-case */
  min-width: v-bind(imageWidthPx);
  /* stylelint-disable-next-line value-keyword-case */
  height: v-bind(imageHeightPx);
  /* stylelint-disable-next-line value-keyword-case */
  min-height: v-bind(imageHeightPx);

  &:hover {
    box-shadow: 0 3px $image-shadow-width 0 var(--app-panel-shadow-color-hover);
  }
}

// Safari does not respect the last right margin,
// unless we have an element after it
.app-details-images__spacer {
  min-width: 1px;
  min-height: 1px;
  opacity: 0;
  user-select: none;
  visibility: hidden;
}
</style>
